import { useI18n } from "@wojo/localization";
import { Markdown, Text } from "@wojo/ui";
import pluralize from "pluralize";
import React, { useId } from "react";
import { LuUtensils } from "react-icons/lu";
import { PricedExcursionFragment } from "../../../generated/graphql";
import styles from "./Itinerary.module.scss";
import { ItineraryExcursion } from "./ItineraryExcursion";
import { ItineraryFreeTime } from "./ItineraryFreeTime";
import { ItineraryHighlights } from "./ItineraryHighlights";
import { ItineraryPhysicalActivity } from "./ItineraryPhysicalActivity";
import { ItinerarySectionTitle } from "./ItinerarySectionTitle";

export type ItineraryDayProps = {
    prefix?: string | null;
    name?: string | null;
    description?: string | null;
    meals?: string[] | null;
    physicalActivity?: string | null;
    excursions?: PricedExcursionFragment[] | null;
    highlights?: string | null;
    freeTimeSuggestions?: string | null;
    isTopLevel?: boolean | null;
};

export const ItineraryDay = React.forwardRef<HTMLDivElement, ItineraryDayProps>(
    (
        {
            prefix = "",
            name = "",
            excursions = [],
            description = "",
            meals = [],
            physicalActivity,
            highlights,
            freeTimeSuggestions,
            isTopLevel,
        },
        ref,
    ) => {
        const titleId = useId();
        const { productPage } = useI18n("productPage");

        const hasTitle = !!(prefix || name);
        const title = hasTitle
            ? productPage.itinerary.day.sectionTitle.l({
                  prefix: prefix ?? "",
                  name: name ?? "",
              })
            : undefined;
        return (
            <section
                className={styles.day}
                aria-labelledby={title ? titleId : undefined}
            >
                <div ref={ref} tabIndex={ref ? -1 : undefined}>
                    <ItinerarySectionTitle
                        isTopLevel={isTopLevel}
                        title={title}
                        id={titleId}
                    />
                </div>

                {!!meals?.length && (
                    <Text.Body
                        style={{
                            display: "inline-flex",
                            alignItems: "start",
                            gap: "var(--g-spacing-sm)",
                            marginBottom: "var(--g-spacing-sm)",
                        }}
                    >
                        <LuUtensils
                            aria-hidden
                            size={20}
                            style={{ minHeight: "1lh" }}
                        />
                        {pluralize(
                            productPage.itinerary.day.mealLabel.l(),
                            meals.length,
                        )}
                        : {meals.join(", ")}
                    </Text.Body>
                )}
                {!!description && <Markdown markdown={description} />}
                <ItineraryPhysicalActivity
                    physicalActivity={physicalActivity}
                />
                {excursions?.map((excursion, index) => (
                    <ItineraryExcursion
                        key={`itinerary-excursion-${index}`}
                        excursion={excursion}
                    />
                ))}
                <ItineraryHighlights highlights={highlights} />
                <ItineraryFreeTime suggestions={freeTimeSuggestions} />
            </section>
        );
    },
);

ItineraryDay.displayName = "ItineraryDay";
